import React, { useEffect, useMemo, useState, ReactNode } from "react";

// UX
import CardDrugTransferInterfaceUX from "./CardDrugTransferInterfaceUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Utils
import { Checkbox, Icon } from "semantic-ui-react";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SaveType,
  FilterSearchType,
  WidthsType,
  GenerateTableHeader,
} from "./sequence/DrugTransferInterface";

// Serializer
import DrugTransferInterfaceSerializer from "issara-sdk/types/DrugTransferInterfaceSerializer_apps_INF";

// Type
type CardDrugTransferInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  DrugTransferInterfaceSequence: State["DrugTransferInterfaceSequence"];
  languageUX: any;
} & PickedProps;

const CardDrugTransferInterface = (props: CardDrugTransferInterfaceProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedDrugTransfer, setSelectedDrugTransfer] = useState<any[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "DrugTransferInterface", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data}</div>;
  };

  const columnTableIconCheck = (data: boolean, style?: any) => {
    return (
      <div style={{ ...style, display: "flex", justifyContent: "center" }}>
        {data === true ? (
          <Icon name="check circle" color="green" size="big" />
        ) : (
          <Icon name="times circle" color="red" size="big" />
        )}
      </div>
    );
  };

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      let newData = (props.DrugTransferInterfaceSequence?.drugTransferInterfaceList || []).map(
        (item: DrugTransferInterfaceSerializer) => item.id
      );

      setSelectedDrugTransfer(newData);
    } else {
      setSelectedDrugTransfer([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "DrugTransferInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      page: 0,
    });

    setSelectedDrugTransfer([]);
  };

  const handleSave = (type: SaveType) => {
    props.runSequence({
      sequence: "DrugTransferInterface",
      action: "SAVE",
      selectedItems: selectedDrugTransfer,
      cardKey: CARD_NAME,
      type,
    });

    setSelectedDrugTransfer([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "DrugTransferInterface",
      action: "CLEAR",
      cardKey: CARD_NAME,
    });

    setSelectedDrugTransfer([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;
    props.setProp(`DrugTransferInterfaceSequence.searchDrugTransfer.${name}`, data?.value);
  };

  const handleChangeDate = (date: string) => {
    props.setProp(`DrugTransferInterfaceSequence.searchDrugTransfer.createdDate`, date);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`DrugTransferInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "DrugTransferInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      [key]: value,
    });

    setSelectedDrugTransfer([]);
    props.setProp(`DrugTransferInterfaceSequence.selectedItems`, []);
  };

  // const handleDisabledTrigger = useMemo(() => {
  //   if (selectedDrugTransfer.length === 0) return true;

  //   return !selectedDrugTransfer.every((id) => {
  //     const row = props.DrugTransferInterfaceSequence?.drugTransferInterfaceList?.find(
  //       (item: DrugTransferInterfaceSerializer) => item.id === id
  //     );

  //     return row?.valid === false;
  //   });
  // }, [selectedDrugTransfer, props.DrugTransferInterfaceSequence?.drugTransferInterfaceList]);

  // const handleDisabledSent = useMemo(() => {
  //   if (selectedDrugTransfer.length === 0) return true;

  //   return !selectedDrugTransfer.every((id) => {
  //     const row = props.DrugTransferInterfaceSequence?.drugTransferInterfaceList?.find(
  //       (item: DrugTransferInterfaceSerializer) => item.id
  //     );

  //     return row?.valid === true && row?.sent === false;
  //   });
  // }, [selectedDrugTransfer, props.DrugTransferInterfaceSequence?.drugTransferInterfaceList]);

  const handleDisabledAddNEdit = useMemo(() => {
    if (selectedDrugTransfer.length === 0) return true;
  }, [selectedDrugTransfer]);

  // useMemo
  const drugTransferInterfaceList = useMemo(() => {
    return (props.DrugTransferInterfaceSequence?.drugTransferInterfaceList || []).map(
      (item: DrugTransferInterfaceSerializer) => {
        let createdName = (props.DrugTransferInterfaceSequence?.userOptions || []).find(
          (data: DrugTransferInterfaceSerializer) => data?.id === item.created_by
        );

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={selectedDrugTransfer.includes(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  setSelectedDrugTransfer([...selectedDrugTransfer, item.id]);
                } else {
                  let newData = selectedDrugTransfer.filter((key: any) => key !== item.id);
                  setSelectedDrugTransfer(newData);
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof DrugTransferInterfaceSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (key === "created_at") {
                data[key] = columnTableCenter(dateToStringBE(item[key]));
              } else if (key === "created_by") {
                data[key] = columnTableCenter(
                  (createdName?.full_name_code || "").replace(/\s*\(.*?\)/g, "")
                );
              } else if (["valid", "sent"].includes(key)) {
                data[key] = columnTableIconCheck(!!item[key]);
              } else if (leftColumns.includes(key)) {
                data[key] = item[key];
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            },
            {}
          ),
        };
      }
    );
  }, [
    selectedDrugTransfer,
    props.DrugTransferInterfaceSequence?.drugTransferInterfaceList,
    props.DrugTransferInterfaceSequence?.userOptions,
    tableHeaders,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardDrugTransferInterfaceUX
        onChangeDate={handleChangeDate}
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchDrugTransfer={props.DrugTransferInterfaceSequence?.searchDrugTransfer}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedDrugTransfer.length ===
                    (props.DrugTransferInterfaceSequence?.drugTransferInterfaceList || []).length &&
                  (props.DrugTransferInterfaceSequence?.drugTransferInterfaceList || []).length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        drugTransferInterfaceList={drugTransferInterfaceList}
        paginatedItems={props.DrugTransferInterfaceSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title={intl.formatMessage({ id: "ค้นหา" })}
            name="SEARCH"
            size="medium"
          />
        }
        addButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("ADD")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_NAME}_ADD`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_ADD`]}
            // config
            color="blue"
            title="Add Record"
            name="ADD"
            size="medium"
          />
        }
        editButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("EDIT")}
            disabled={handleDisabledAddNEdit}
            // data
            paramKey={`${CARD_NAME}_EDIT`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_EDIT`]}
            // config
            color="green"
            title="Edit Date"
            name="EDIT"
            size="medium"
          />
        }
      />
    </>
  );
};

CardDrugTransferInterface.displayName = "CardDrugTransferInterface";

export default React.memo(CardDrugTransferInterface);

const leftColumns: Partial<keyof DrugTransferInterfaceSerializer>[] = [
  "error_message",
  "file_name",
  "document_header_text",
];

const columnWidths: WidthsType = {
  id: 70,
  valid: 70,
  error_message: 150,
  created_by: 120,
  created_at: 150,
  sent: 70,
  file_name: 160,
  version: 80,
  document_date: 120,
  movement_type: 120,
};

const tableHeaders = GenerateTableHeader(columnWidths);
